/* eslint-disable @nrwl/nx/enforce-module-boundaries */
import { useState } from 'react';
import { observer } from 'mobx-react';
import { Alert } from '@material-ui/lab';
import { Theme } from '@material-ui/core/styles';
import { makeStyles } from '@material-ui/core/styles';
import { createStyles } from '@material-ui/core/styles';
import { Box, Grid, Typography } from '@material-ui/core';
import { Button, TextField } from '@roc/ui';
import { isNotBlank } from '@roc/client-portal-shared/utils';
import { useStore } from '../../hooks';
import ValuationDocumentSignModal from './components/valuationDocumentSignModal';


const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    successAlert: {
      backgroundColor: 'transparent',
      '& .MuiAlert-icon': {
        marginRight: theme.spacing(1),
      },
    },
    borrowerInfoGrid: {
      padding: theme.spacing(4),
    },
  })
);

interface Props {
  handleBackStep: () => void;
  handleNextStep: () => void;
}

const AcknowledgmentStep = observer((props: Props) => {
  const [documentSignError, setDocumentSignError] = useState<boolean>(false);
  const [modalOpen, setModalOpen] = useState<boolean>(false);
  const [email, setEmail] = useState<string>('');
  const [fullNameError, setFullNameError] = useState<string>('');
  const [emailError, setEmailError] = useState<string>('');
  const { valuationPaymentStore } = useStore();
  const { documentSignStatus, cardHolderName } = valuationPaymentStore;
  const classes = useStyles();

  const handleModalClose = () => setModalOpen(false);

  const handleModalOpen = () => {
    let hasError = false;
    const trimmedCardHolderName = cardHolderName.trim();

    if (!trimmedCardHolderName) {
      setFullNameError('Name is required');
      hasError = true;
    } else if (!/^[A-Za-z\s]+$/.test(trimmedCardHolderName)) {
      setFullNameError('Name should contain only alphabetic characters');
      hasError = true;
    }

    if (!email.trim()) {
      setEmailError('Email is required');
      hasError = true;
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setEmailError('Email format is invalid');
      hasError = true;
    }

    if (!hasError) {
      valuationPaymentStore.setCardHolderName(trimmedCardHolderName);
      setModalOpen(true);
    }
  };

  const handleFullNameChange = (value: string) => {
    valuationPaymentStore.setCardHolderName(value);
    setFullNameError('');
  };

  const handleEmailChange = (value: string) => {
    setEmail(value);
    setEmailError('');
  };

  return (
    <>
      <Box style={{ maxWidth: 840, margin: 'auto' }}>
        <Box>
          <Box mb={2}>
            <Typography variant="h4">
              Valuation Payment Authorization
            </Typography>
          </Box>
        </Box>

        <Box mt={3}>
          <Typography variant="body1">
            Please review the Valuation Payment Authorization Form carefully before continuing. Once you
            have read and agree to the terms, please sign and accept it to proceed
            to the payment for the valuation service.
          </Typography>
          <br />
          <br />
          <Typography variant="h6">CardHolder's Information</Typography>
          <br />
          <Grid container direction="row" spacing={3}>
            <Grid item xs={12} md={6} className={classes.borrowerInfoGrid}>
              <TextField
                testId="fullName"
                variant="outlined"
                standaloneLabel
                label="CardHolder's Name"
                placeholder="(as displayed in card)"
                value={cardHolderName}
                onChange={e => handleFullNameChange(e.target.value)}
                disabled={documentSignStatus}
                fullWidth
                helperText={fullNameError}
                error={isNotBlank(fullNameError)}
              />
            </Grid>
            <Grid item xs={12} md={6} className={classes.borrowerInfoGrid}>
              <TextField
                testId="email"
                variant="outlined"
                standaloneLabel
                label="CardHolder's Email"
                value={email}
                onChange={e => handleEmailChange(e.target.value)}
                disabled={documentSignStatus}
                fullWidth
                helperText={emailError}
                error={isNotBlank(emailError)}
              />
            </Grid>
          </Grid>
          <Box mb={1} mt={3}>
            <Grid container>
              <Button
                variant="contained"
                color="primary"
                onClick={handleModalOpen}
                disabled={documentSignStatus || !cardHolderName.trim() || !email}
                size="large"
                testId="acceptTermsAndSign"
              >
                {'Review & Sign'}
              </Button>
              {documentSignStatus && (
                <Alert className={classes.successAlert} severity="success">
                  Accepted
                </Alert>
              )}
            </Grid>
          </Box>
        </Box>

        {documentSignError && (
          <Box mt={3}>
            <Alert severity="error">
              <Typography variant="body1">
                There was an error processing your signature. Please try again.
              </Typography>
            </Alert>
          </Box>
        )}

        <Box mt={16}>
          <Grid container justifyContent="center" spacing={2}>
            <Grid item xs={6} md={3}>
              <Button
                variant="outlined"
                color="primary"
                fullWidth
                onClick={props.handleBackStep}
                testId="back"
              >
                Back
              </Button>
            </Grid>
            <Grid item xs={6} md={3}>
              <Button
                variant="contained"
                color="primary"
                disabled={!documentSignStatus}
                fullWidth
                onClick={props.handleNextStep}
                testId="next"
              >
                Make Payment
              </Button>
            </Grid>
          </Grid>
        </Box>
      </Box>

      <ValuationDocumentSignModal
        onClose={handleModalClose}
        open={modalOpen}
        successPage={documentSignStatus || documentSignError}
      />
    </>
  );
});

export default AcknowledgmentStep;
